// Uang lebih dari pemasok — K215 / BUSINESS-90 (keputusan Owner 14 Okt 2026): uang kembali pemasok yang MELEBIHI
// jumlah yang disepakati tetap dicatat Finance; kelebihannya diselesaikan: Admin Purchasing memilih DIKEMBALIKAN ke
// pemasok atau DIPOTONGKAN ke tagihan berikutnya pemasok yang sama, disetujui Kepala Cabang (pola penyelesaian retur
// BUSINESS-27). Yang dikembalikan ditransfer balik Finance, lalu Finance mencatat tanggal + bukti transfernya.
//
// Tombol mengikuti peran; pagar sesungguhnya di backend (`backend/services/uang_lebih_pemasok.py`). MODE KLIEN
// (pola SisaUangKembaliPanel): himpunannya kecil secara struktur dan dihitung utuh di server.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { StatusBadge } from "../../components/etams/StatusBadge";
import ProofUploader from "../../components/etams/ProofUploader";
import { formatIDR, formatDate, todayISO } from "../../lib/format";
import { apiErrorMessage } from "../../lib/purchasingApi";
import { mintaTeks } from "../../lib/dialogAplikasi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const KOLOM = [
  { kunci: "nomor", judul: "Nomor" },
  { kunci: "vendor_nama", judul: "Pemasok" },
  { kunci: "nilai_diharapkan_idr", judul: "Disepakati" },
  { kunci: "total_diterima_idr", judul: "Diterima" },
  { kunci: "kelebihan_idr", judul: "Uang lebih" },
  { kunci: "belum_dipilih_idr", judul: "Belum dipilih" },
];
const TONE_STATUS = { MENUNGGU_PERSETUJUAN: "warning", MENUNGGU_DIKEMBALIKAN: "warning", SELESAI: "success", DITOLAK: "danger",
  DILEPAS: "neutral" };   // K219: potongan dilepas karena tagihannya dibatalkan
const tautan = (s) => (s.sumber === "RETUR_VENDOR" ? "/purchasing/returns" : `/purchasing/dp/${s.sumber_id}`);

/** `sumber` = "RETUR_VENDOR" / "UANG_MUKA_VENDOR" — hanya sumber itu yang tampil; kosong = semua (layar Uang Masuk). */
export default function UangLebihPemasokPanel({ sumber, testId = "uang-lebih" }) {
  const { roleCode } = useAuth();
  const [items, setItems] = useState(null);
  const [pilih, setPilih] = useState(null);          // item yang sedang dipilih penyelesaiannya (Admin Purchasing)
  const [isiPilih, setIsiPilih] = useState({ pilihan: "DIKEMBALIKAN", invoice_id: "", nilai: "", catatan: "" });
  const [kembali, setKembali] = useState(null);      // penyelesaian yang dicatat sudah dikembalikan (Finance)
  const [isiKembali, setIsiKembali] = useState({ tanggal: todayISO(), bukti: null, catatan: "" });
  const [sibuk, setSibuk] = useState(false);
  const daftar = useDaftar(items || [], {
    awalan: "ul_", ukuran: 10, kolom: KOLOM, kunciCari: ["nomor", "vendor_nama"],
    urutAwal: { kunci: "belum_dipilih_idr", arah: "desc" },
  });

  const muat = useCallback(async () => {
    try {
      const r = await api.get("/uang-masuk/uang-lebih-pemasok");
      setItems((r.data.items || []).filter((s) => !sumber || s.sumber === sumber));
    } catch (e) {
      setItems([]);
      toast.error(apiErrorMessage(e, "Daftar uang lebih dari pemasok belum bisa dimuat."));
    }
  }, [sumber]);
  useEffect(() => { muat(); }, [muat]);

  if (items === null || items.length === 0) return null;

  const bukaPilih = (s) => {
    setPilih(s);
    setIsiPilih({ pilihan: "DIKEMBALIKAN", invoice_id: "", nilai: String(s.belum_dipilih_idr || ""), catatan: "" });
  };
  const ajukan = async () => {
    setSibuk(true);
    try {
      await api.post("/uang-masuk/uang-lebih-pemasok", {
        sumber: pilih.sumber, sumber_id: pilih.sumber_id, pilihan: isiPilih.pilihan,
        invoice_id: isiPilih.pilihan === "POTONG_TAGIHAN" ? isiPilih.invoice_id || null : null,
        nilai_idr: Number(isiPilih.nilai) || null, catatan: isiPilih.catatan.trim() || null,
      });
      toast.success("Pilihan diajukan ke Kepala Cabang."); setPilih(null); muat();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSibuk(false); }
  };
  const putus = async (p, setuju) => {
    let alasan = null;
    if (!setuju) {
      alasan = await mintaTeks("Alasan menolak pilihan ini:", { minPanjang: 5 });
      if (!alasan) return;
    }
    setSibuk(true);
    try {
      await api.post(`/uang-masuk/uang-lebih-pemasok/${p.id}/putus`, { setuju, alasan });
      toast.success(setuju ? "Pilihan disetujui." : "Pilihan ditolak."); muat();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSibuk(false); }
  };
  const bukaKembali = (p, s) => { setKembali({ ...p, induk: s }); setIsiKembali({ tanggal: todayISO(), bukti: null, catatan: "" }); };
  const catatKembali = async () => {
    setSibuk(true);
    try {
      await api.post(`/uang-masuk/uang-lebih-pemasok/${kembali.id}/dikembalikan`, {
        tanggal: isiKembali.tanggal, bukti_file_id: isiKembali.bukti, catatan: isiKembali.catatan.trim() || null,
      });
      toast.success("Pengembalian ke pemasok tercatat."); setKembali(null); muat();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSibuk(false); }
  };
  const lihatBukti = async (id) => {
    try { const r = await api.get(`/uang-masuk/uang-lebih-pemasok/${id}/bukti`); window.open(r.data.url, "_blank", "noopener"); }
    catch (e) { toast.error(apiErrorMessage(e, "Bukti belum bisa dibuka.")); }
  };

  const perluTindakan = items.filter((s) => s.belum_dipilih_idr > 0 || s.menunggu_persetujuan.length || s.menunggu_dikembalikan.length).length;
  const tagihan = pilih?.tagihan_bisa_dipotong || [];

  return (
    <Card className="p-4 space-y-3" data-testid={testId}>
      <div className="font-medium flex items-center gap-2">
        <Coins className="h-4 w-4" /> Uang lebih dari pemasok ({items.length}){perluTindakan ? ` · ${perluTindakan} perlu ditindaklanjuti` : ""}
      </div>
      <p className="text-xs text-muted-foreground">
        Pemasok mengembalikan uang lebih dari yang disepakati. Uangnya tetap dicatat Finance; kelebihannya dipilih Admin
        Purchasing — dikembalikan ke pemasok atau dipotongkan ke tagihan berikutnya pemasok yang sama — lalu disetujui
        Kepala Cabang. Yang dikembalikan ditransfer Finance dan dicatat dengan bukti transfernya.
      </p>
      <div className="overflow-x-auto">
        <PalangDaftar daftar={daftar} placeholder="Cari nomor atau pemasok…" testId={`${testId}-palang`}
                      ekspor={{ nama: "uang_lebih_pemasok", kolom: KOLOM }} />
        <table className="w-full text-sm" data-testid={`${testId}-tabel`}>
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <ThUrut daftar={daftar} kunci="nomor" className="py-1 pr-2">Nomor</ThUrut>
              <ThUrut daftar={daftar} kunci="vendor_nama" className="py-1 pr-2">Pemasok</ThUrut>
              <ThUrut daftar={daftar} kunci="nilai_diharapkan_idr" align="right" className="py-1 pr-2 text-right">Disepakati</ThUrut>
              <ThUrut daftar={daftar} kunci="total_diterima_idr" align="right" className="py-1 pr-2 text-right">Diterima</ThUrut>
              <ThUrut daftar={daftar} kunci="kelebihan_idr" align="right" className="py-1 pr-2 text-right">Uang lebih</ThUrut>
              <th className="py-1 pr-2">Penyelesaian</th>
            </tr>
          </thead>
          <tbody>
            {daftar.tampil.length === 0 && (
              <tr><td colSpan={6} className="py-2 text-muted-foreground">Tidak ada yang cocok.</td></tr>
            )}
            {daftar.tampil.map((s) => (
              <tr key={`${s.sumber}-${s.sumber_id}`} className="border-b last:border-0 align-top" data-testid={`${testId}-${s.sumber_id}`}>
                <td className="py-1 pr-2">
                  <div className="text-xs text-muted-foreground">{s.sumber_label}</div>
                  <Link to={tautan(s)} className="font-mono underline">{s.nomor || "—"}</Link>
                </td>
                <td className="py-1 pr-2">{s.vendor_nama || "—"}</td>
                <td className="py-1 pr-2 text-right font-mono">{formatIDR(s.nilai_diharapkan_idr)}</td>
                <td className="py-1 pr-2 text-right font-mono">{formatIDR(s.total_diterima_idr)}</td>
                <td className="py-1 pr-2 text-right font-mono font-medium">{formatIDR(s.kelebihan_idr)}</td>
                <td className="py-1 pr-2 space-y-1 text-xs">
                  {s.penyelesaian.map((p) => (
                    <div key={p.id} data-testid={`${testId}-p-${p.id}`}>
                      <StatusBadge tone={TONE_STATUS[p.status]}>{p.status_label}</StatusBadge>{" "}
                      {p.pilihan_label} {formatIDR(p.nilai_idr)}
                      {p.alasan_tolak ? <span className="text-muted-foreground"> · alasan: {p.alasan_tolak}</span> : null}
                      {p.dikembalikan_tanggal ? (
                        <span className="text-muted-foreground"> · ditransfer {formatDate(p.dikembalikan_tanggal)}{" "}
                          <button type="button" className="underline" onClick={() => lihatBukti(p.id)}>bukti</button>
                        </span>
                      ) : null}
                      {roleCode === "KEPALA_CABANG" && p.status === "MENUNGGU_PERSETUJUAN" && (
                        <span className="ml-1 inline-flex gap-1">
                          <Button size="sm" disabled={sibuk} onClick={() => putus(p, true)} data-testid={`${testId}-setuju-${p.id}`}>Setujui</Button>
                          <Button size="sm" variant="outline" disabled={sibuk} onClick={() => putus(p, false)} data-testid={`${testId}-tolak-${p.id}`}>Tolak</Button>
                        </span>
                      )}
                      {roleCode === "FINANCE" && p.status === "MENUNGGU_DIKEMBALIKAN" && (
                        <Button size="sm" variant="outline" className="ml-1" disabled={sibuk} onClick={() => bukaKembali(p, s)}
                                data-testid={`${testId}-kembali-${p.id}`}>Catat sudah dikembalikan</Button>
                      )}
                    </div>
                  ))}
                  {s.belum_dipilih_idr > 0 && (
                    <div>
                      <StatusBadge tone="warning">Belum dipilih {formatIDR(s.belum_dipilih_idr)}</StatusBadge>
                      {roleCode === "ADMIN_PURCHASING" && (
                        <Button size="sm" variant="outline" className="ml-1" disabled={sibuk} onClick={() => bukaPilih(s)}
                                data-testid={`${testId}-pilih-${s.sumber_id}`}>Pilih penyelesaian</Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId={`${testId}-paginasi`} />
      </div>

      <Dialog open={!!pilih} onOpenChange={(o) => { if (!o) setPilih(null); }}>
        <DialogContent data-testid={`${testId}-modal-pilih`}>
          <DialogHeader><DialogTitle>Penyelesaian uang lebih — {pilih?.nomor}</DialogTitle></DialogHeader>
          {pilih && (
            <div className="space-y-3 text-sm">
              <div>{pilih.vendor_nama || "—"} · uang lebih belum dipilih {formatIDR(pilih.belum_dipilih_idr)}</div>
              <select className="h-9 w-full rounded-md border bg-background px-2" value={isiPilih.pilihan}
                      onChange={(e) => setIsiPilih({ ...isiPilih, pilihan: e.target.value })} data-testid={`${testId}-pilihan`}>
                <option value="DIKEMBALIKAN">Dikembalikan ke pemasok (Finance mentransfer balik)</option>
                <option value="POTONG_TAGIHAN">Dipotongkan ke tagihan pemasok yang sama</option>
              </select>
              {isiPilih.pilihan === "POTONG_TAGIHAN" && (
                tagihan.length ? (
                  <select className="h-9 w-full rounded-md border bg-background px-2" value={isiPilih.invoice_id}
                          onChange={(e) => setIsiPilih({ ...isiPilih, invoice_id: e.target.value })} data-testid={`${testId}-tagihan`}>
                    <option value="">— pilih tagihan yang dipotong —</option>
                    {tagihan.map((t) => (
                      <option key={t.id} value={t.id}>{t.internal_number} · sisa {formatIDR(t.sisa_idr)}</option>
                    ))}
                  </select>
                ) : (
                  <div className="text-xs text-muted-foreground" data-testid={`${testId}-tanpa-tagihan`}>
                    Belum ada tagihan pemasok ini di cabang yang sama yang sudah disetujui dan belum lunas. Tunggu
                    tagihan berikutnya, atau pilih dikembalikan ke pemasok.
                  </div>
                )
              )}
              <div>
                <Label>Nilai (Rp)</Label>
                <Input inputMode="numeric" value={isiPilih.nilai} data-testid={`${testId}-nilai`}
                       onChange={(e) => setIsiPilih({ ...isiPilih, nilai: e.target.value.replace(/[^0-9]/g, "") })} />
                <div className="text-xs text-muted-foreground mt-1">Boleh sebagian; sisanya dipilih lagi nanti.</div>
              </div>
              <Textarea rows={2} placeholder="Catatan (opsional)" value={isiPilih.catatan}
                        onChange={(e) => setIsiPilih({ ...isiPilih, catatan: e.target.value })} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPilih(null)}>Batal</Button>
            <Button onClick={ajukan} data-testid={`${testId}-ajukan`}
                    disabled={sibuk || !Number(isiPilih.nilai) || (isiPilih.pilihan === "POTONG_TAGIHAN" && !isiPilih.invoice_id)}>
              Ajukan ke Kepala Cabang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!kembali} onOpenChange={(o) => { if (!o) setKembali(null); }}>
        <DialogContent data-testid={`${testId}-modal-kembali`}>
          <DialogHeader><DialogTitle>Uang lebih dikembalikan ke pemasok — {kembali?.induk?.nomor}</DialogTitle></DialogHeader>
          {kembali && (
            <div className="space-y-3 text-sm">
              <div>{kembali.induk?.vendor_nama || "—"} · {formatIDR(kembali.nilai_idr)}</div>
              <ProofUploader testIdPrefix={`${testId}-upload`}
                             endpoint={`/uang-masuk/uang-lebih-pemasok/${kembali.id}/bukti`}
                             onUploaded={(r) => setIsiKembali((x) => ({ ...x, bukti: r.bukti_file_id }))} />
              <div>
                <Label>Tanggal transfer *</Label>
                <Input type="date" value={isiKembali.tanggal} max={todayISO()} data-testid={`${testId}-tanggal`}
                       onChange={(e) => setIsiKembali({ ...isiKembali, tanggal: e.target.value })} />
              </div>
              <Input placeholder="Catatan (opsional)" value={isiKembali.catatan}
                     onChange={(e) => setIsiKembali({ ...isiKembali, catatan: e.target.value })} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setKembali(null)}>Batal</Button>
            <Button onClick={catatKembali} disabled={sibuk || !isiKembali.bukti} data-testid={`${testId}-simpan-kembali`}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
